/**
 * Responsibility: Implement tts behavior for the engine layer.
 * Scope: Owns engine-layer concerns only; cross-layer orchestration is excluded.
 */
const STORAGE_TTS_ENABLED = 'am2_ttsenabled'
const MAX_TTS_CHARS = 420
const MAX_TTS_QUEUE = 6

let enabled = readEnabled()
let queue: string[] = []
let isSpeaking = false

function readEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_TTS_ENABLED) === 'true'
  } catch {
    return false
  }
}

function writeEnabled(value: boolean): void {
  try {
    localStorage.setItem(STORAGE_TTS_ENABLED, String(value))
  } catch {
    // ignored
  }
}

export function isTtsSupported(): boolean {
  return typeof window !== 'undefined' &&
    typeof window.speechSynthesis !== 'undefined' &&
    typeof window.SpeechSynthesisUtterance !== 'undefined'
}

export function isTtsEnabled(): boolean {
  if (!isTtsSupported()) return false
  return enabled
}

export function setTtsEnabled(value: boolean): boolean {
  enabled = !!value && isTtsSupported()
  writeEnabled(enabled)
  if (!enabled) stopNarration()
  return enabled
}

export function toggleTts(): boolean {
  return setTtsEnabled(!isTtsEnabled())
}

export function stopNarration(graceful = false): void {
  if (!isTtsSupported()) return
  queue = []
  if (graceful) {
    // Let the current utterance finish naturally, just don't start more
    return
  }
  isSpeaking = false
  try {
    window.speechSynthesis.cancel()
  } catch {
    // ignored
  }
}

export function sanitizeNarrationForTts(raw: string): string {
  const text = String(raw ?? '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/[#>~_]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  return text.slice(0, MAX_TTS_CHARS)
}

export function speakNarration(raw: string): boolean {
  if (!isTtsEnabled()) return false
  const text = sanitizeNarrationForTts(raw)
  if (!text) return false
  queue.push(text)
  if (queue.length > MAX_TTS_QUEUE) queue = queue.slice(-MAX_TTS_QUEUE)
  maybeSpeakNextQueued()
  return true
}

function maybeSpeakNextQueued(): void {
  if (!isTtsEnabled() || isSpeaking) return
  const text = queue.shift()
  if (!text) return
  try {
    const utterance = new window.SpeechSynthesisUtterance(text)
    utterance.rate = 1
    utterance.pitch = 1
    utterance.volume = 0.95
    utterance.onend = () => {
      isSpeaking = false
      maybeSpeakNextQueued()
    }
    utterance.onerror = () => {
      isSpeaking = false
      maybeSpeakNextQueued()
    }
    isSpeaking = true
    window.speechSynthesis.speak(utterance)
  } catch {
    isSpeaking = false
  }
}
